// Sprint 2026-Feb-13 — Approve Estimate Dialog
//
// Opened by the Approvals Inbox when the approver clicks Approve on
// an *estimate* request. Presents the four spec-mandated radio
// options and dynamically shows only the relevant field sections:
//
//   ○ Just Approve                       — no data change
//   ○ Update Work Completion Date       — one date input
//   ○ Update Voucher Row Details        — order#/month/server + apply-to-all
//   ○ Update Both                       — union of the above
//
// After Confirm, calls POST /approvals/{rid}/approve with
//   { comment, updates_before_approve: { kind, data } }
// The backend applies the field updates atomically BEFORE advancing
// the approval state. Any validation failure keeps the request Pending.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, X, AlertCircle, ClipboardCheck } from "lucide-react";

const KINDS = [
  { key: "just_approve",    label: "Just Approve",
    desc: "No changes to the estimate — approve as-is." },
  { key: "work_date",       label: "Update Mail Approval Date & Approve",
    desc: "Set the Mail Approval Date, then approve." },
  { key: "voucher_details", label: "Update Voucher Details & Approve",
    desc: "Set Order Number / Order Month / Client Server, then approve." },
  { key: "both",            label: "Update Both & Approve",
    desc: "Update Mail Approval Date AND row details, then approve." },
];

function formatCurrentDate(v) {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  try {
    return new Date(s).toLocaleDateString("en-IN",
      { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
}

function monthPickerValue(v) {
  // Accepts "July-2026" or "2026-07" — returns YYYY-MM for the input.
  if (!v) return "";
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})$/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  // Fallback try Date.parse
  try {
    const d = new Date(s);
    if (!isNaN(d)) {
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      return `${d.getFullYear()}-${mm}`;
    }
  } catch { /* ignore */ }
  return "";
}
function monthPickerToDisplay(v) {
  // Convert "YYYY-MM" from the picker back to "Month-YYYY" for storage.
  if (!v) return "";
  const m = String(v).match(/^(\d{4})-(\d{2})$/);
  if (!m) return v;
  const months = ["January","February","March","April","May","June",
                  "July","August","September","October","November","December"];
  return `${months[parseInt(m[2], 10) - 1]}-${m[1]}`;
}

export default function ApproveEstimateDialog({ request, estimate, onClose, onDone }) {
  const [kind, setKind] = useState("just_approve");
  const [comment, setComment] = useState("");
  const [wcd, setWcd] = useState("");
  // Bulk row fields
  const [applyAll, setApplyAll] = useState(true);
  const [orderNumber, setOrderNumber] = useState("");
  const [orderMonth, setOrderMonth] = useState("");
  const [clientServer, setClientServer] = useState("");
  // Per-line edits when applyAll = false
  const [perLine, setPerLine] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!estimate) return;
    // Pre-fill current values as read-only hints (per spec §4a).
    // Mail Approval Date (renamed 2026-Mar-01 from work_completion_date).
    setWcd(String(estimate.mail_approval_date || "").slice(0, 10));
    const firstRow = (estimate.line_items || [])[0] || {};
    setOrderNumber(firstRow.order_number || "");
    setOrderMonth(monthPickerValue(firstRow.order_month || ""));
    setClientServer(firstRow.client_server || "");
    setPerLine((estimate.line_items || []).map((r, i) => ({
      index: i,
      description: r.description || `Line ${i + 1}`,
      order_number: r.order_number || "",
      order_month:  monthPickerValue(r.order_month || ""),
      client_server: r.client_server || "",
    })));
  }, [estimate]);

  const showWorkDate = kind === "work_date" || kind === "both";
  const showVoucher  = kind === "voucher_details" || kind === "both";

  const isEstimate = String(request?.module || "").toLowerCase() === "estimate";
  const canConfirm = useMemo(() => {
    if (busy) return false;
    if (kind === "just_approve") return true;
    if (kind === "work_date" || kind === "both") {
      if (!wcd) return false;
    }
    if (kind === "voucher_details" || kind === "both") {
      if (applyAll) {
        if (!orderNumber && !orderMonth && !clientServer) return false;
      } else {
        if (!perLine.some(l => l.order_number || l.order_month || l.client_server)) return false;
      }
    }
    return true;
  }, [busy, kind, wcd, applyAll, orderNumber, orderMonth, clientServer, perLine]);

  async function confirm() {
    setBusy(true);
    try {
      const body = { comment };
      if (kind !== "just_approve") {
        const data = {};
        if (kind === "work_date" || kind === "both") {
          data.mail_approval_date = wcd;
        }
        if (kind === "voucher_details" || kind === "both") {
          if (applyAll) {
            data.voucher_details = {
              apply_to_all: true,
              order_number:  orderNumber || "",
              order_month:   monthPickerToDisplay(orderMonth),
              client_server: clientServer || "",
            };
          } else {
            data.voucher_details = {
              apply_to_all: false,
              per_line: perLine
                .filter(l => l.order_number || l.order_month || l.client_server)
                .map(l => ({
                  index: l.index,
                  order_number:  l.order_number  || "",
                  order_month:   monthPickerToDisplay(l.order_month),
                  client_server: l.client_server || "",
                })),
            };
          }
        }
        body.updates_before_approve = { kind, data };
      }
      await api.post(`/approvals/${request.id}/approve`, body);
      const kindLabel = KINDS.find(k => k.key === kind)?.label || "Approved";
      toast.success(kind === "just_approve"
        ? "Approved"
        : `Approved — ${kindLabel}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setBusy(false);
    }
  }

  if (!isEstimate) return null;   // safety — should never open for non-estimate

  return createPortal(
    <>
      <div className="fixed inset-0 z-[200] bg-black/50" onClick={onClose}
           data-testid="approve-estimate-backdrop" />
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col pointer-events-auto"
             data-testid={`approve-estimate-dialog-${request.id}`}>
          <div className="px-5 py-3 border-b flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-800 text-sm inline-flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4 text-emerald-600" />
                Approve Estimate
              </h3>
              <div className="text-[10px] text-slate-500 font-mono">
                {estimate?.estimate_number || request?.document_number || request?.document_id}
              </div>
            </div>
            <button onClick={onClose}
                    className="text-slate-400 hover:text-slate-700 text-xl leading-none"
                    data-testid="approve-close">×</button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* ── Radio picker ── */}
            <div className="border border-slate-200">
              <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Choose Approval Action
              </div>
              <div className="p-3 space-y-2">
                {KINDS.map(k => (
                  <label key={k.key}
                         data-testid={`approve-radio-${k.key}`}
                         className={`flex items-start gap-2 p-2 border cursor-pointer ${
                           kind === k.key
                             ? "border-orange-500 bg-orange-50"
                             : "border-slate-200 hover:border-slate-300"
                         }`}>
                    <input type="radio" name="approve-kind"
                           checked={kind === k.key}
                           onChange={() => setKind(k.key)}
                           className="mt-1" />
                    <div className="flex-1">
                      <div className="text-xs font-bold text-slate-800">{k.label}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">{k.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* ── Mail Approval Date section ── */}
            {showWorkDate && (
              <div className="border border-slate-200" data-testid="section-work-date">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  Mail Approval Date
                </div>
                <div className="p-3 space-y-2">
                  <div className="text-[10px] text-slate-500">
                    Current: <span className="font-mono text-slate-700">
                      {formatCurrentDate(estimate?.mail_approval_date)}
                    </span>
                  </div>
                  <div className="max-w-xs">
                    <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      New Mail Approval Date
                    </Label>
                    <Input type="date" value={wcd}
                           onChange={e => setWcd(e.target.value)}
                           data-testid="approve-wcd" />
                  </div>
                </div>
              </div>
            )}

            {/* ── Voucher row details section ── */}
            {showVoucher && (
              <div className="border border-slate-200" data-testid="section-voucher-details">
                <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Voucher Row Details
                  </div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600 inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={applyAll}
                           onChange={e => setApplyAll(e.target.checked)}
                           data-testid="approve-apply-all" />
                    Apply same values to all line items
                  </label>
                </div>
                <div className="p-3 space-y-3">
                  {applyAll ? (
                    <>
                      <div className="text-[10px] text-slate-500">
                        Current (first line):
                        <span className="ml-1 font-mono text-slate-700">
                          {(estimate?.line_items?.[0]?.order_number || "—")}
                          {" · "}
                          {(estimate?.line_items?.[0]?.order_month  || "—")}
                          {" · "}
                          {(estimate?.line_items?.[0]?.client_server || "—")}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <Field label="Order Number">
                          <Input value={orderNumber}
                                 onChange={e => setOrderNumber(e.target.value)}
                                 placeholder="PO-4587"
                                 data-testid="approve-order-number" />
                        </Field>
                        <Field label="Order Month">
                          <Input type="month" value={orderMonth}
                                 onChange={e => setOrderMonth(e.target.value)}
                                 data-testid="approve-order-month" />
                        </Field>
                        <Field label="Client Server">
                          <Input value={clientServer}
                                 onChange={e => setClientServer(e.target.value)}
                                 placeholder="Server-A"
                                 data-testid="approve-client-server" />
                        </Field>
                      </div>
                    </>
                  ) : (
                    <div className="max-h-80 overflow-y-auto border border-slate-100">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5 text-[9px] uppercase font-bold text-slate-500 w-8">#</th>
                            <th className="text-left px-2 py-1.5 text-[9px] uppercase font-bold text-slate-500">Description</th>
                            <th className="text-left px-2 py-1.5 text-[9px] uppercase font-bold text-slate-500">Order #</th>
                            <th className="text-left px-2 py-1.5 text-[9px] uppercase font-bold text-slate-500">Order Month</th>
                            <th className="text-left px-2 py-1.5 text-[9px] uppercase font-bold text-slate-500">Client Server</th>
                          </tr>
                        </thead>
                        <tbody>
                          {perLine.map((l, i) => (
                            <tr key={l.index} className="border-t border-slate-100">
                              <td className="px-2 py-1.5 text-slate-400">{l.index + 1}</td>
                              <td className="px-2 py-1.5 max-w-[220px] truncate" title={l.description}>{l.description}</td>
                              <td className="px-2 py-1.5">
                                <Input value={l.order_number}
                                       onChange={e => setPerLine(pl => pl.map((row, k) => k === i ? { ...row, order_number: e.target.value } : row))}
                                       className="h-7 text-xs"
                                       data-testid={`approve-line-order-number-${l.index}`} />
                              </td>
                              <td className="px-2 py-1.5">
                                <Input type="month" value={l.order_month}
                                       onChange={e => setPerLine(pl => pl.map((row, k) => k === i ? { ...row, order_month: e.target.value } : row))}
                                       className="h-7 text-xs"
                                       data-testid={`approve-line-order-month-${l.index}`} />
                              </td>
                              <td className="px-2 py-1.5">
                                <Input value={l.client_server}
                                       onChange={e => setPerLine(pl => pl.map((row, k) => k === i ? { ...row, client_server: e.target.value } : row))}
                                       className="h-7 text-xs"
                                       data-testid={`approve-line-client-server-${l.index}`} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Approval Remark (optional)
              </Label>
              <Textarea rows={2} value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder="Any note for the audit trail…"
                        data-testid="approve-comment" />
            </div>

            {kind !== "just_approve" && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2 flex items-start gap-2"
                   data-testid="approve-audit-notice">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                All changes will be recorded in the audit log tagged
                <b className="mx-1">Updated During Approval</b> along with your
                user, timestamp, old and new values.
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}
                    data-testid="approve-cancel">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button onClick={confirm} disabled={!canConfirm}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="approve-confirm">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {busy ? "Approving…" : "Confirm & Approve"}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
